import { useCallback, useState } from "react";

import { useDndMonitor } from "@dnd-kit/core";
import {
  faBriefcase,
  faCalculatorSimple,
  faCube,
  faGear,
  faQuestionCircle,
  faRobot,
  faSearch,
  faTimer,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useOutletContext } from "react-router-dom";

import { Flow } from "src/api/flowTypes";
import { Divider } from "src/base-components/Divider";
import { FeatureGated } from "src/base-components/FeatureGated";
import { Pill } from "src/base-components/Pill";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useIsFlowDndAvailable } from "src/flowsOverview/v2/FlowDndProvider";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { AppHeaderLogo } from "src/layout/AppHeaderLogo";
import { ClickableSearchBox } from "src/layout/ClickableSearchBox";
import {
  DecisionFlowsNavItem,
  NavItem,
  NavSidebar,
  RetoolNavItem,
} from "src/layout/NavSideBar";
import { UserMenu } from "src/layout/UserMenuV2";
import { useCollapsibleMainSidebar } from "src/mainPage/mainSidebarStore";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import { useRetoolInformation } from "src/retool/useRetoolInformation";
import {
  DashboardPageParams,
  getUrlToAgentsPage,
  getUrlToCaseQueuePage,
  getUrlToEventsPage,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";
import { canAccessSettingsPage } from "src/settings/utils";
import { modifierKey } from "src/utils/platformModifierKey";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DndSidebarMonitor: React.FC<{
  onDragChange: (isDragging: boolean) => void;
}> = ({ onDragChange }) => {
  useDndMonitor({
    onDragStart: () => onDragChange(true),
    onDragEnd: () => onDragChange(false),
    onDragCancel: () => onDragChange(false),
  });
  return null;
};

const CaseManagementNavItem = ({
  capabilities,
  orgId,
  wsId,
  onDoubleClick,
}: {
  capabilities: ReturnType<typeof useCapabilities>;
  orgId: string;
  wsId: string;
  onDoubleClick?: () => void;
}) => {
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  if (!capabilities.cases.canAccess || !isCaseManagementEnabled) {
    return null;
  }

  return (
    <NavItem
      end={false}
      icon={faBriefcase}
      title="Cases"
      to={getUrlToCaseQueuePage({ orgId, wsId })}
      onDoubleClick={onDoubleClick ?? undefined}
    />
  );
};

export const MainSideBar: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { collapsed, setCollapsed } = useCollapsibleMainSidebar();
  const context = useOutletContext();
  const { data: eventTypes } = useEventSchemas();
  const { flow } = context as { flow: Flow | undefined };

  const { showOmnibox } = useOmniboxActions();
  const capabilities = useCapabilities();
  const { agentsEnabled } = useWorkspaceFeatureGates();
  const { isEntityViewPage, isFlowPage, isAuthoringPage, isSettingsPage } =
    useCurrentPage();
  const { retoolPageUrl, retoolConnection, isCurrentPageRetool } =
    useRetoolInformation();

  const shouldBeCollapsed =
    isEntityViewPage || isFlowPage || isAuthoringPage || isSettingsPage;

  const isDndAvailable = useIsFlowDndAvailable();
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerOverSidebar, setIsPointerOverSidebar] = useState(false);

  const isDragHovering = isDragging && isPointerOverSidebar;

  // The sidebar is collapsed when:
  // - The user has collapsed the sidebar and is not dragging (a flow)
  // - The current page is a page that does not allow the sidebar to be uncollapsed
  const isCollapsed = (collapsed && !isDragHovering) || shouldBeCollapsed;

  const toggleSidebar = useCallback(() => {
    setCollapsed?.(!collapsed);
  }, [setCollapsed, collapsed]);

  return (
    <div
      className="flex h-full flex-col"
      onPointerEnter={
        isDragging ? () => setIsPointerOverSidebar(true) : undefined
      }
      onPointerLeave={
        isDragging ? () => setIsPointerOverSidebar(false) : undefined
      }
    >
      {isDndAvailable && <DndSidebarMonitor onDragChange={setIsDragging} />}
      <div className="mb-5 px-3.5">
        <Link
          to={getUrlToWsDashboard({
            orgId,
            wsId,
            folderId: flow?.flow_folder_id,
          })}
        >
          <div className="mt-4 mb-7 flex items-center gap-x-2">
            <AppHeaderLogo dataLoc="header-taktile-logo" />
            {!isCollapsed && (
              <p className="font-inter-semibold-13px text-gray-800">Taktile</p>
            )}
          </div>
        </Link>
        {isCollapsed ? (
          <div className="py-1">
            {/* The py-1 is to account for the space added by the search box. This way the menu below does not move when the sidebar is collapsed*/}
            <Tooltip
              placement="bottom-end"
              title={
                <>
                  <span className="mr-2">Search</span>
                  <Pill size="sm" variant="dark-gray">
                    <Pill.Text fontType="text">{modifierKey} K</Pill.Text>
                  </Pill>
                </>
              }
              asChild
            >
              <IconButton icon={faSearch} size="xs" onClick={showOmnibox} />
            </Tooltip>
          </div>
        ) : (
          <ClickableSearchBox
            dataLoc="main-sidebar-search"
            text="Search"
            onClick={showOmnibox}
          />
        )}
      </div>
      <div className="px-2.5">
        <Divider />
      </div>
      <div className="min-h-0 flex-1">
        <NavSidebar
          collapsed={isCollapsed}
          isDragHovering={isDragHovering}
          setCollapsed={shouldBeCollapsed ? null : setCollapsed}
          wide
        >
          {capabilities.agents.canAccess && agentsEnabled && (
            <NavItem
              end={false}
              icon={faRobot}
              // Using a larger icon size for the agents icon to match the other icons in the sidebar.
              iconSize="sm"
              title="Agents"
              to={getUrlToAgentsPage({ orgId, wsId })}
              onDoubleClick={toggleSidebar}
            />
          )}
          <DecisionFlowsNavItem
            orgId={orgId}
            wsId={wsId}
            onDoubleClick={toggleSidebar}
          />

          <FeatureGated requiredGate="entitiesEnabled">
            <NavItem
              end={false}
              icon={faCube}
              title="Entities"
              to={getUrlToWsDashboard({
                orgId,
                wsId,
                page: WsDashboardPageCode.Entities,
              })}
              onDoubleClick={toggleSidebar}
            />
          </FeatureGated>

          <FeatureGated requiredGate="featuresEventsEnabled">
            <NavItem
              end={false}
              icon={faTimer}
              title="Events"
              to={getUrlToEventsPage({
                orgId,
                wsId,
                eventType: eventTypes?.at(0)?.event_type,
              })}
              onDoubleClick={toggleSidebar}
            />
            <NavItem
              end={false}
              icon={faCalculatorSimple}
              title="Features"
              to={getUrlToWsDashboard({
                orgId,
                wsId,
                page: WsDashboardPageCode.Features,
              })}
              onDoubleClick={toggleSidebar}
            />
          </FeatureGated>
          <CaseManagementNavItem
            capabilities={capabilities}
            orgId={orgId}
            wsId={wsId}
            onDoubleClick={toggleSidebar}
          />
          {retoolPageUrl && (
            <RetoolNavItem
              dataLoc="retool-icon"
              isCurrentPageRetool={isCurrentPageRetool}
              retoolConnectionName={retoolConnection?.name ?? ""}
              retoolPageUrl={retoolPageUrl}
              onDoubleClick={toggleSidebar}
            />
          )}

          <div className="mr-2.5">
            <Divider spacing="my-2" />
          </div>

          <NavItem
            end={false}
            icon={faQuestionCircle}
            title="Documentation"
            to="https://docs.taktile.com"
            externalLink={true}
            onDoubleClick={toggleSidebar}
          />

          {canAccessSettingsPage(capabilities) && (
            <NavItem
              dataLoc="header-settings-icon"
              end={false}
              icon={faGear}
              title="Settings"
              to={getUrlToSettingsPage(orgId, wsId)}
            />
          )}

          <li
            className="mr-2.5 flex items-center gap-x-2 rounded-lg p-1.5 hover:bg-gray-50"
            data-loc="main-sidebar-profile-content"
          >
            <UserMenu showUsername={!isCollapsed} variant="sidebar" />
          </li>
        </NavSidebar>
      </div>
    </div>
  );
};
